import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
} from 'react';
// Define types locally since shared package is not available
interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  subscription?: any;
  isVerified?: boolean;
}

interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  token?: string; // Legacy support
}
import { api, setRefreshTokenFunction } from '../utils/apiInterceptor';
import { shouldRefreshToken } from '../utils/tokenUtils';
import {
  secureStorageSet,
  secureStorageGet,
  secureStorageRemove,
} from '../utils/encryption';

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
  refreshAccessToken: () => Promise<boolean>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

type AuthAction =
  | { type: 'AUTH_START' }
  | {
      type: 'AUTH_SUCCESS';
      payload: { user: User; token: string; refreshToken?: string };
    }
  | { type: 'AUTH_FAILURE' }
  | {
      type: 'TOKEN_REFRESHED';
      payload: { token: string; refreshToken: string };
    }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'LOGOUT' };

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken || state.refreshToken,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
      };
    case 'TOKEN_REFRESHED':
      return {
        ...state,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        refreshToken: null,
        isLoading: false,
      };
    default:
      return state;
  }
};

const initialState: AuthState = {
  user: null,
  token: secureStorageGet('token'),
  refreshToken: secureStorageGet('refreshToken'),
  isLoading: true,
  isAuthenticated: false,
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const refreshIntervalRef = useRef<number | null>(null);

  const refreshAccessToken = async (): Promise<boolean> => {
    const refreshToken = secureStorageGet('refreshToken');
    if (!refreshToken) {
      console.log('[AuthContext] No refresh token available');
      return false;
    }

    try {
      console.log('[AuthContext] Attempting to refresh access token');
      const responseData = await api.post('/auth/refresh', {
        refreshToken,
      }) as { token: string; refreshToken: string };
      const { token: newToken, refreshToken: newRefreshToken } = responseData;

      secureStorageSet('token', newToken);
      secureStorageSet('refreshToken', newRefreshToken);

      dispatch({
        type: 'TOKEN_REFRESHED',
        payload: { token: newToken, refreshToken: newRefreshToken },
      });

      console.log('[AuthContext] Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('[AuthContext] Token refresh failed:', error);
      // If refresh fails, clear all tokens and log out
      secureStorageRemove('token');
      secureStorageRemove('refreshToken');
      dispatch({ type: 'AUTH_FAILURE' });
      return false;
    }
  };

  // Start proactive token refresh interval
  const startRefreshInterval = () => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Check token every minute and refresh if needed
    refreshIntervalRef.current = window.setInterval(() => {
      const token = secureStorageGet('token');
      if (token && state.isAuthenticated && shouldRefreshToken(token, 5)) {
        console.log('[AuthContext] Proactive token refresh triggered');
        refreshAccessToken();
      }
    }, 60000); // Check every minute
  };

  // Stop proactive token refresh interval
  const stopRefreshInterval = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  };

  // Set up the refresh function for the API interceptor
  useEffect(() => {
    setRefreshTokenFunction(refreshAccessToken);
  }, []);

  // Start/stop refresh interval based on authentication state
  useEffect(() => {
    if (state.isAuthenticated && state.token) {
      startRefreshInterval();
    } else {
      stopRefreshInterval();
    }

    // Cleanup on unmount
    return () => stopRefreshInterval();
  }, [state.isAuthenticated, state.token]);

  useEffect(() => {
    const token = secureStorageGet('token');
    if (token) {
      // Verify token and get user profile
      api
        .get('/auth/profile')
        .then(response => {
          console.log('[AuthContext] Profile response:', response);
          const data = response.data;
          // Handle subscription updates
          if (data.subscriptionUpdated) {
            console.log(
              '[AuthContext] Subscription updated, refreshing tokens'
            );
            secureStorageSet('token', data.token);
            secureStorageSet('refreshToken', data.refreshToken);
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                user: data.user,
                token: data.token,
                refreshToken: data.refreshToken,
              },
            });
          } else {
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                user: data?.user || data,
                token,
              },
            });
          }
        })
        .catch(() => {
          secureStorageRemove('token');
          dispatch({ type: 'AUTH_FAILURE' });
        });
    } else {
      dispatch({ type: 'AUTH_FAILURE' });
    }
  }, []);

  const login = async (email: string, password: string) => {
    console.log('[AuthContext] Starting login process');
    dispatch({ type: 'AUTH_START' });
    try {
      console.log('[AuthContext] Making secure login API request');

      // TEMPORARY: Use plain credentials for debugging
      const responseData = await api.post('/auth/login', {
        email,
        password,
      }) as AuthResponse;

      console.log('[AuthContext] Response data received:', responseData);

      if (!responseData) {
        throw new Error('No response received from server');
      }

      console.log('[AuthContext] Login API response received:', {
        hasUser: !!responseData.user,
        hasToken: !!responseData.token,
        response: responseData,
      });

      // Handle the actual API response structure (interceptor returns data directly)
      const user = responseData?.user;
      const token = responseData?.token;
      const refreshToken = responseData?.refreshToken;

      if (!user || !token) {
        throw new Error('Invalid response from server: missing user or token');
      }

      // Use secure storage for tokens
      secureStorageSet('token', token);
      if (refreshToken) {
        secureStorageSet('refreshToken', refreshToken);
      }
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token, refreshToken },
      });
      console.log('[AuthContext] Login successful, user authenticated');
    } catch (error) {
      console.error('[AuthContext] Login failed:', error);
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    dispatch({ type: 'AUTH_START' });
    try {
      // TEMPORARY: Use plain credentials for debugging
      // TODO: Implement server-side decryption before using encrypted payloads
      const responseData = (await api.post(
        '/auth/register',
        data
      )) as AuthResponse;
      const user = responseData?.user;
      const token = responseData?.token;
      const refreshToken = responseData?.refreshToken;

      if (!user || !token) {
        throw new Error('Invalid response from server');
      }

      // Use secure storage
      secureStorageSet('token', token);
      if (refreshToken) {
        secureStorageSet('refreshToken', refreshToken);
      }
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token, refreshToken },
      });
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE' });
      throw error;
    }
  };

  const logout = () => {
    secureStorageRemove('token');
    secureStorageRemove('refreshToken');
    dispatch({ type: 'LOGOUT' });
  };

  const updateUser = (user: User) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
    refreshAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
